import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  FaEdit,
  FaEye,
  FaPlus,
  FaSearch,
  FaTag,
  FaTimes,
  FaTrash,
} from "react-icons/fa";
import AdminSidebar from "../Navbar/AdminSidebar"; 
import AdminNavbar from "../../Admin/Navbar/Navbar1";// Adjust only this path if required.
import "./AdminCoupons.css";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const apiClient = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem("accessToken");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const EMPTY_FORM = {
  code: "",
  name: "",
  description: "",
  discountType: "PERCENTAGE",
  discountValue: "",
  minimumOrderValue: 0,
  maximumDiscount: "",
  applicableTo: "ALL",
  validFrom: "",
  validUntil: "",
  totalUsageLimit: "",
  usageLimitPerUser: 1,
  status: "ACTIVE",
};

const unwrap = (response) => response?.data?.data || response?.data || {};

const AdminCoupons = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [viewingCoupon, setViewingCoupon] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });

  const fetchCoupons = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.get("/admin/coupons", {
        params: { page, limit: 10, search: search.trim() || undefined, status: status || undefined },
      });
      const data = unwrap(response);
      setCoupons(Array.isArray(data.coupons) ? data.coupons : []);
      setPagination(data.pagination || { page: 1, totalPages: 1, total: 0 });
    } catch (error) {
      toast.error(error.response?.data?.message || "Could not load coupons", {
        position: "top-center",
      });
    } finally {
      setLoading(false);
    }
  }, [page, search, status]);

  useEffect(() => {
    const timeout = setTimeout(fetchCoupons, 300);
    return () => clearTimeout(timeout);
  }, [fetchCoupons]);

  const stats = useMemo(() => {
    const now = new Date();
    return {
      total: pagination.total || coupons.length,
      active: coupons.filter((c) => c.status === "ACTIVE").length,
      expired: coupons.filter((c) => new Date(c.validUntil) < now).length,
      uses: coupons.reduce((sum, c) => sum + Number(c.usedCount || 0), 0),
    };
  }, [coupons, pagination.total]);

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...EMPTY_FORM });
    setFormOpen(true);
  };

  const openEdit = (coupon) => {
    setEditingId(coupon._id);
    setForm({
      ...EMPTY_FORM,
      ...coupon,
      validFrom: coupon.validFrom?.slice(0, 10) || "",
      validUntil: coupon.validUntil?.slice(0, 10) || "",
      maximumDiscount: coupon.maximumDiscount ?? "",
      totalUsageLimit: coupon.totalUsageLimit ?? "",
    });
    setFormOpen(true);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: name === "code" ? value.toUpperCase() : value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      discountValue: Number(form.discountValue),
      minimumOrderValue: Number(form.minimumOrderValue || 0),
      maximumDiscount: form.maximumDiscount === "" ? null : Number(form.maximumDiscount),
      totalUsageLimit: form.totalUsageLimit === "" ? null : Number(form.totalUsageLimit),
      usageLimitPerUser: Number(form.usageLimitPerUser || 1),
    };

    try {
      if (editingId) {
        await apiClient.patch(`/admin/coupons/${editingId}`, payload);
        toast.success("Coupon updated successfully", { position: "top-center" });
      } else {
        await apiClient.post("/admin/coupons", payload);
        toast.success("Coupon created successfully", { position: "top-center" });
      }
      setFormOpen(false);
      await fetchCoupons();
    } catch (error) {
      toast.error(error.response?.data?.message || "Could not save coupon", {
        position: "top-center",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (coupon) => {
    if (!window.confirm(`Delete coupon ${coupon.code}?`)) return;
    try {
      await apiClient.delete(`/admin/coupons/${coupon._id}`);
      toast.success("Coupon deleted", { position: "top-center" });
      fetchCoupons();
    } catch (error) {
      toast.error(error.response?.data?.message || "Could not delete coupon", {
        position: "top-center",
      });
    }
  };

  return (
    <div className="coupon-admin-layout">
      <AdminSidebar />
      <AdminNavbar/>
      
      <main className="coupon-admin-main">
        <header className="coupon-page-header">
          <div>
            <span>Marketplace Management</span>
            <h1>Coupons</h1>
            <p>Create and control promotional discounts for products and services.</p>
          </div>
          <button className="coupon-primary-button" type="button" onClick={openCreate}>
            <FaPlus /> Add Coupon
          </button>
        </header>

        <section className="coupon-stat-grid">
          <StatCard label="Total Coupons" value={stats.total} />
          <StatCard label="Active on Page" value={stats.active} tone="green" />
          <StatCard label="Expired on Page" value={stats.expired} tone="red" />
          <StatCard label="Uses on Page" value={stats.uses} tone="maroon" />
        </section>

        <section className="coupon-table-card">
          <div className="coupon-toolbar">
            <div className="coupon-search"><FaSearch /><input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="Search code or coupon name" /></div>
            <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
              <option value="">All statuses</option>
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
            </select>
          </div>

          <div className="coupon-table-wrapper">
            <table>
              <thead><tr><th>Coupon</th><th>Discount</th><th>Applicability</th><th>Validity</th><th>Usage</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {loading ? <tr><td colSpan="7" className="coupon-empty">Loading coupons...</td></tr> : coupons.length === 0 ? <tr><td colSpan="7" className="coupon-empty"><FaTag /> No coupons found</td></tr> : coupons.map((coupon) => (
                  <tr key={coupon._id}>
                    <td><strong className="coupon-code">{coupon.code}</strong><small>{coupon.name}</small></td>
                    <td><strong>{coupon.discountType === "PERCENTAGE" ? `${coupon.discountValue}%` : `₹${Number(coupon.discountValue).toLocaleString("en-IN")}`}</strong><small>Min. ₹{Number(coupon.minimumOrderValue || 0).toLocaleString("en-IN")}</small></td>
                    <td>{coupon.applicableTo.replaceAll("_", " ")}</td>
                    <td><strong>{formatDate(coupon.validFrom)}</strong><small>to {formatDate(coupon.validUntil)}</small></td>
                    <td>{coupon.usedCount || 0} / {coupon.totalUsageLimit || "∞"}</td>
                    <td><span className={`coupon-status ${coupon.status.toLowerCase()}`}>{coupon.status}</span></td>
                    <td><div className="coupon-actions"><button title="View" onClick={() => setViewingCoupon(coupon)}><FaEye /></button><button title="Edit" onClick={() => openEdit(coupon)}><FaEdit /></button><button className="delete" title="Delete" onClick={() => handleDelete(coupon)}><FaTrash /></button></div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="coupon-pagination"><span>{pagination.total || 0} coupons</span><div><button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</button><span>Page {page} of {pagination.totalPages || 1}</span><button disabled={page >= (pagination.totalPages || 1)} onClick={() => setPage((p) => p + 1)}>Next</button></div></div>
        </section>
      </main>

      {formOpen && <CouponFormModal form={form} editing={Boolean(editingId)} saving={saving} onChange={handleChange} onClose={() => setFormOpen(false)} onSubmit={handleSubmit} />}
      {viewingCoupon && <CouponViewModal coupon={viewingCoupon} onClose={() => setViewingCoupon(null)} onEdit={() => { openEdit(viewingCoupon); setViewingCoupon(null); }} />}
    </div>
  );
};

const StatCard = ({ label, value, tone = "default" }) => <article className={`coupon-stat-card ${tone}`}><span>{label}</span><strong>{value}</strong></article>;
const formatDate = (date) => date ? new Date(date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const CouponFormModal = ({ form, editing, saving, onChange, onClose, onSubmit }) => (
  <div className="coupon-modal-backdrop"><section className="coupon-modal" role="dialog" aria-modal="true"><header><div><span>{editing ? "Update promotion" : "New promotion"}</span><h2>{editing ? "Edit Coupon" : "Add Coupon"}</h2></div><button type="button" onClick={onClose}><FaTimes /></button></header><form onSubmit={onSubmit}><div className="coupon-form-grid">
    <Field label="Coupon Code" name="code" value={form.code} onChange={onChange} required />
    <Field label="Coupon Name" name="name" value={form.name} onChange={onChange} required />
    <Field label="Description" name="description" value={form.description} onChange={onChange} textarea full />
    <Select label="Discount Type" name="discountType" value={form.discountType} onChange={onChange} options={[["PERCENTAGE","Percentage"],["FIXED","Fixed Amount"]]} />
    <Field label="Discount Value" name="discountValue" type="number" min="0" value={form.discountValue} onChange={onChange} required />
    <Field label="Minimum Order Value" name="minimumOrderValue" type="number" min="0" value={form.minimumOrderValue} onChange={onChange} />
    <Field label="Maximum Discount" name="maximumDiscount" type="number" min="0" value={form.maximumDiscount} onChange={onChange} />
    <Select label="Applicable To" name="applicableTo" value={form.applicableTo} onChange={onChange} options={[["ALL","All Offerings"],["PRODUCTS","Products Only"],["SERVICES","Services Only"]]} />
    <Field label="Valid From" name="validFrom" type="date" value={form.validFrom} onChange={onChange} required />
    <Field label="Valid Until" name="validUntil" type="date" value={form.validUntil} onChange={onChange} required />
    <Field label="Total Usage Limit" name="totalUsageLimit" type="number" min="1" value={form.totalUsageLimit} onChange={onChange} />
    <Field label="Usage Limit Per User" name="usageLimitPerUser" type="number" min="1" value={form.usageLimitPerUser} onChange={onChange} />
    <Select label="Status" name="status" value={form.status} onChange={onChange} options={[["ACTIVE","Active"],["INACTIVE","Inactive"]]} />
  </div><footer><button type="button" className="coupon-secondary-button" onClick={onClose}>Cancel</button><button type="submit" className="coupon-primary-button" disabled={saving}>{saving ? "Saving..." : editing ? "Update Coupon" : "Create Coupon"}</button></footer></form></section></div>
);

const CouponViewModal = ({ coupon, onClose, onEdit }) => (
  <div className="coupon-modal-backdrop"><section className="coupon-modal coupon-view-modal" role="dialog" aria-modal="true"><header><div><span>Coupon Details</span><h2>{coupon.code}</h2><p>{coupon.name}</p></div><button type="button" onClick={onClose}><FaTimes /></button></header><div className="coupon-detail-grid"><Detail label="Description" value={coupon.description} full /><Detail label="Discount" value={coupon.discountType === "PERCENTAGE" ? `${coupon.discountValue}%` : `₹${coupon.discountValue}`} /><Detail label="Minimum Order" value={`₹${coupon.minimumOrderValue || 0}`} /><Detail label="Maximum Discount" value={coupon.maximumDiscount ? `₹${coupon.maximumDiscount}` : "No limit"} /><Detail label="Applicable To" value={coupon.applicableTo?.replaceAll("_", " ")} /><Detail label="Valid From" value={formatDate(coupon.validFrom)} /><Detail label="Valid Until" value={formatDate(coupon.validUntil)} /><Detail label="Total Usage" value={`${coupon.usedCount || 0} / ${coupon.totalUsageLimit || "Unlimited"}`} /><Detail label="Per-user Limit" value={coupon.usageLimitPerUser} /><Detail label="Status" value={coupon.status} /></div><footer><button className="coupon-secondary-button" onClick={onClose}>Close</button><button className="coupon-primary-button" onClick={onEdit}><FaEdit /> Edit Coupon</button></footer></section></div>
);

const Field = ({ label, textarea, full, ...props }) => <label className={full ? "full" : ""}><span>{label}</span>{textarea ? <textarea rows="3" {...props} /> : <input {...props} />}</label>;
const Select = ({ label, options, ...props }) => <label><span>{label}</span><select {...props}>{options.map(([value, text]) => <option key={value} value={value}>{text}</option>)}</select></label>;
const Detail = ({ label, value, full }) => <div className={full ? "full" : ""}><span>{label}</span><strong>{value || value === 0 ? value : "Not added"}</strong></div>;

export default AdminCoupons;
